import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL } from './constants';

const TOKEN_STORAGE_KEY = 'pt.auth.token';

export interface ApiError {
  status: number;
  code: string;
  message: string;
  detail?: unknown;
}

let memoryToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  memoryToken = token;
  if (typeof window !== 'undefined') {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }
}

export function getAuthToken(): string | null {
  if (memoryToken) return memoryToken;
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  memoryToken = stored;
  return stored;
}

export function registerUnauthorizedHandler(handler: () => void): () => void {
  onUnauthorized = handler;
  return () => {
    if (onUnauthorized === handler) onUnauthorized = null;
  };
}

function normalizeError(error: AxiosError<ApiError>): ApiError {
  const status = error.response?.status ?? 0;
  const data = error.response?.data;
  return {
    status,
    code: (data && 'code' in data && typeof data.code === 'string'
      ? data.code
      : error.code ?? 'unknown_error'),
    message:
      (data && 'message' in data && typeof data.message === 'string'
        ? data.message
        : error.message) || '请求失败，请稍后再试。',
    detail: data,
  };
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAuthToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // FormData 上传时必须删掉默认 application/json,让浏览器自动加 boundary
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const normalized = normalizeError(error);

    if (normalized.status === 401) {
      setAuthToken(null);
      onUnauthorized?.();
      toast.error('登录状态已失效，请重新登录。');
    } else if (normalized.status >= 500) {
      toast.error('服务暂时不可用，请稍后再试。');
    } else if (normalized.status !== 0) {
      toast.error(normalized.message);
    }

    return Promise.reject(normalized);
  },
);

export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.get<T>(url, config);
  return res.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.post<T>(url, body, config);
  return res.data;
}

export async function apiPut<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.put<T>(url, body, config);
  return res.data;
}

export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await api.delete<T>(url, config);
  return res.data;
}

export async function apiUpload<T>(
  url: string,
  formData: FormData,
  onUploadProgress?: (percent: number) => void,
): Promise<T> {
  const res = await api.post<T>(url, formData, {
    onUploadProgress: (event) => {
      if (!onUploadProgress || !event.total) return;
      onUploadProgress(Math.round((event.loaded * 100) / event.total));
    },
  });
  return res.data;
}